import { useState } from "react";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { loadRazorpay } from "@/components/pricing/CheckoutDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { money } from "@/lib/plan";

export function TopUpDialog({ open, packs, onClose, onDone }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(null);
  const cur = user?.billing_currency || "INR";
  const buy = async (pack) => {
    setBusy(pack.id);
    try {
      const { data } = await api.post("/billing/topup-checkout", { pack_id: pack.id });
      if (!(await loadRazorpay())) throw new Error("Couldn't load the payment window. Check your connection and try again.");
      const rzp = new window.Razorpay({
        key: data.key_id, order_id: data.order_id, amount: data.amount, currency: data.currency, name: "IDDCR LearnAI",
        description: data.plan_name, prefill: data.prefill, theme: { color: "#2878F0" },
        modal: { ondismiss: () => setBusy(null) },
        handler: async (resp) => {
          try { await api.post("/billing/verify", resp); toast.success(`${pack.requests} extra AI requests added!`); onDone(); } catch (e) { toast.error(formatError(e)); }
          setBusy(null);
        },
      });
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed. You have not been charged."); setBusy(null); });
      onClose();
      rzp.open();
    } catch (e) { toast.error(formatError(e)); setBusy(null); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="topup-dialog">
        <DialogHeader>
          <DialogTitle>Buy extra AI requests</DialogTitle>
          <DialogDescription>Extra requests are used only after your plan's monthly AI requests run out. They're valid for 90 days. One-time payment.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {packs.map((p) => (
            <button key={p.id} type="button" data-testid={`topup-pack-${p.id}`} disabled={!!busy} onClick={() => buy(p)}
              className="flex flex-col items-start gap-1 rounded-2xl border border-line p-5 text-left transition-transform hover:-translate-y-0.5 hover:border-aqua disabled:opacity-60">
              <Zap className="h-5 w-5 text-aqua" />
              <span className="mt-2 font-display text-2xl font-extrabold text-ink">{p.requests}</span>
              <span className="text-xs text-ink-3">extra AI requests</span>
              <span data-testid={`topup-price-${p.id}`} className="mt-2 font-semibold text-ink">{busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : money(cur === "USD" ? p.price_usd : p.price_inr, cur)}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-ink-3">By paying you agree to the <a href="/legal/terms" target="_blank" className="underline">Terms</a> and <a href="/legal/refunds" target="_blank" className="underline">Refund Policy</a>.</p>
      </DialogContent>
    </Dialog>
  );
}
